import React from 'react'
import {KTSVG, toAbsoluteUrl} from '../../../../helpers'
import {ResultItem, SearchType} from '../../../../../app/pages/search/types'
import {DropdownGroup} from '../../../../../app/pages/search/utils'
import {describeRow} from '../../../../../app/pages/search/rowView'
import {defaultProfileImage} from '../../../../../app/utils/constants'
import {iconForType, labelForType} from './searchTypeMeta'

interface ResultsDropdownProps {
  groups: DropdownGroup[]
  query: string
  totalCount: number
  /** Flat index of the keyboard-active row across all groups (-1 = none). */
  activeIndex?: number
  /** id of the listbox element; option ids derive from it for aria-activedescendant. */
  listboxId: string
  onSelectRow: (item: ResultItem) => void
  /** Per-group "See all <type> results →": always navigates to that type's page with the
   *  current query preserved. */
  onSeeAll: (type: SearchType) => void
  /** Footer "See all results →": if the selector scope is a specific type, the caller drops
   *  the query so the destination shows the full listing. */
  onSeeAllFooter: () => void
  now?: number
}

/** One result row. */
const ResultRow: React.FC<{
  item: ResultItem
  searchType: DropdownGroup['searchType']
  active: boolean
  optionId: string
  onSelect: () => void
  now?: number
}> = ({item, searchType, active, optionId, onSelect, now}) => {
  const view = describeRow(item, now)
  return (
    <button
      type='button'
      role='option'
      id={optionId}
      aria-selected={active}
      className={`nb-gs__row${active ? ' nb-gs__row--active' : ''}`}
      // Keep focus on the input; the row is driven via aria-activedescendant, not real focus.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onSelect}
    >
      <span className={`nb-gs__row-thumb nb-gs__row-thumb--${view.thumbShape}`}>
        {view.thumbShape === 'icon' || !view.thumbUrl ? (
          <KTSVG path={iconForType(searchType)} className='svg-icon-2 nb-gs__row-thumb-icon' />
        ) : (
          <img
            src={toAbsoluteUrl(view.thumbUrl)}
            alt=''
            onError={(e) => {
              e.currentTarget.src = defaultProfileImage
            }}
          />
        )}
      </span>
      <span className='nb-gs__row-text'>
        <span className='nb-gs__row-title'>{view.title}</span>
        {view.meta.length > 0 && <span className='nb-gs__row-meta'>{view.meta.join(' · ')}</span>}
      </span>
    </button>
  )
}

/**
 * GS-02 — the results dropdown. Groups ordered by hit count, ≤5 rows/group, ≤30 total, zero-result
 * groups hidden. Each group has a header `[icon] Type (n)` and a `See all … results →` footer; a
 * fixed `See all results →` row sits at the bottom. Renders an empty state when a response returned
 * with no results.
 */
export const ResultsDropdown: React.FC<ResultsDropdownProps> = ({
  groups,
  query,
  totalCount,
  activeIndex = -1,
  listboxId,
  onSelectRow,
  onSeeAll,
  onSeeAllFooter,
  now,
}) => {
  const isEmpty = totalCount === 0 && groups.length === 0

  return (
    <div className='nb-gs__dropdown' role='listbox' id={listboxId} aria-label='Search results'>
      {isEmpty ? (
        <div className='nb-gs__empty'>
          <div className='nb-gs__empty-title'>No results for &ldquo;{query}&rdquo;</div>
          <div className='nb-gs__empty-hint'>Try a different type or keyword</div>
        </div>
      ) : (
        <>
          <div className='nb-gs__groups'>
            {groups.map((group, gi) => {
              // Flat index offset of this group's first row (for keyboard highlighting).
              const offset = groups.slice(0, gi).reduce((n, g) => n + g.items.length, 0)
              return (
                <div className='nb-gs__group' key={group.bucketKey}>
                  <button
                    type='button'
                    className='nb-gs__group-header'
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onSeeAll(group.searchType)}
                    aria-label={`See all ${labelForType(group.searchType).toLowerCase()} results`}
                  >
                    <KTSVG path={iconForType(group.searchType)} className='svg-icon-4 nb-gs__group-icon' />
                    <span className='nb-gs__group-name'>
                      {labelForType(group.searchType)}
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr001.svg'
                        className='svg-icon-5 nb-gs__group-arrow'
                      />
                    </span>
                    <span className='nb-gs__group-count'>{group.total} Matches</span>
                  </button>
                  {group.items.map((item, ri) => (
                    <ResultRow
                      key={`${group.bucketKey}-${item.id}`}
                      item={item}
                      searchType={group.searchType}
                      active={offset + ri === activeIndex}
                      optionId={`${listboxId}-opt-${offset + ri}`}
                      onSelect={() => onSelectRow(item)}
                      now={now}
                    />
                  ))}
                  <button
                    type='button'
                    className='nb-gs__see-all nb-gs__see-all--group'
                    onClick={() => onSeeAll(group.searchType)}
                  >
                    See all {labelForType(group.searchType).toLowerCase()} results →
                  </button>
                </div>
              )
            })}
          </div>
          <button type='button' className='nb-gs__see-all nb-gs__see-all--footer' onClick={onSeeAllFooter}>
            See all results →
          </button>
        </>
      )}
    </div>
  )
}
